import { useEffect, useRef } from "react";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getRoot, type EditorState } from "lexical";

import {
  activeDraftIdStorage,
  deriveTitleAndPreview,
  type BackupStorage,
  type DraftStorage,
  type PersistedMedia,
  type SerializedEditorStateJson,
} from "../../../lib/storage/index.js";
import { logger } from "../../../lib/logger.js";
import { useDraftsStore } from "../../../stores/drafts-store.js";
import {
  useMediaStore,
  type MediaAttachment,
} from "../../../stores/media-store.js";

const AUTOSAVE_DEBOUNCE_MS = 1000;

function attachmentToPersisted(
  attachment: MediaAttachment | null,
): PersistedMedia | null {
  // Only "ready" uploads have a Telegram file_id worth persisting. In-flight
  // and errored uploads are session-local; we drop them on draft save.
  if (!attachment || attachment.status !== "ready" || !attachment.fileId) {
    return null;
  }
  return {
    kind: attachment.kind,
    fileId: attachment.fileId,
    name: attachment.name,
    mime: attachment.mime,
    size: attachment.size,
    ...(attachment.thumbDataUrl
      ? { thumbDataUrl: attachment.thumbDataUrl }
      : {}),
  };
}

interface UseAutosaveDeps {
  drafts: DraftStorage;
  backup: BackupStorage;
  cloudAvailable: boolean;
}

/**
 * Subscribes to editor changes and persists them.
 *
 * - Always writes the latest state into localStorage (best-effort backup
 *   for reload recovery), regardless of CloudStorage availability.
 * - When the user has typed actual content AND CloudStorage is available:
 *   - if no activeDraftId yet → mint one and adopt it as the buffer's id
 *     so subsequent edits sync to CloudStorage.
 *   - then save the JSON to CloudStorage under that id, debounced.
 * - In-flight saves don't drop newer content: when a save is running and
 *   another flush comes in, the latest state is captured into a dirty
 *   slot and re-flushed as soon as the in-flight save completes.
 */
export function useAutosave(deps: UseAutosaveDeps): void {
  const [editor] = useLexicalComposerContext();
  const activeDraftId = useDraftsStore((s) => s.activeDraftId);
  const upsertMeta = useDraftsStore((s) => s.upsertMeta);
  const setActiveDraftId = useDraftsStore((s) => s.setActiveDraftId);

  // Refs so the registered listener doesn't need to re-bind on every change.
  const activeRef = useRef(activeDraftId);
  activeRef.current = activeDraftId;
  const depsRef = useRef(deps);
  depsRef.current = deps;
  const upsertRef = useRef(upsertMeta);
  upsertRef.current = upsertMeta;
  const setActiveRef = useRef(setActiveDraftId);
  setActiveRef.current = setActiveDraftId;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;
    let pendingState: EditorState | null = null;

    const isContentful = (state: EditorState): boolean => {
      let has = false;
      state.read(() => {
        has = $getRoot().getTextContent().trim().length > 0;
      });
      return has;
    };

    const performSave = (state: EditorState): void => {
      const json = state.toJSON() as unknown as SerializedEditorStateJson;

      // Backup is sync and never fails fatally.
      try {
        depsRef.current.backup.saveCurrent(json);
      } catch (err) {
        logger.warn("backup.saveCurrent failed", err);
      }

      // CloudStorage path requires availability AND either text content
      // or a ready media attachment (a media-only post is valid).
      if (!depsRef.current.cloudAvailable) return;
      const media = attachmentToPersisted(
        useMediaStore.getState().attachment,
      );
      if (!isContentful(state) && !media) return;

      // Promote the unnamed buffer to a tracked draft on first content.
      if (!activeRef.current) {
        const newId = crypto.randomUUID();
        activeRef.current = newId;
        setActiveRef.current(newId);
      }
      const id = activeRef.current;

      if (inFlight) {
        // Don't drop the latest state — capture it for a follow-up flush.
        pendingState = state;
        return;
      }

      const { title } = deriveTitleAndPreview(json);
      inFlight = true;
      depsRef.current.drafts
        .save(id, json, title, media)
        .then((meta) => {
          upsertRef.current(meta);
        })
        .catch((err: unknown) => {
          logger.error("drafts.save failed", err);
        })
        .finally(() => {
          inFlight = false;
          if (pendingState) {
            const next = pendingState;
            pendingState = null;
            performSave(next);
          }
        });
    };

    const unregister = editor.registerUpdateListener(({ editorState }) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        performSave(editorState);
      }, AUTOSAVE_DEBOUNCE_MS);
    });

    // Media changes don't fire editor update listeners — subscribe
    // directly so attaching/removing media flushes a save against the
    // current editor state.
    const unsubMedia = useMediaStore.subscribe((s, prev) => {
      // Only re-save when the persistable shape changes — ignore
      // intermediate "uploading" → "ready" file_id-less transitions.
      const before = attachmentToPersisted(prev.attachment);
      const after = attachmentToPersisted(s.attachment);
      if (before?.fileId === after?.fileId) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        performSave(editor.getEditorState());
      }, AUTOSAVE_DEBOUNCE_MS);
    });

    return () => {
      if (timer) clearTimeout(timer);
      unregister();
      unsubMedia();
    };
  }, [editor]);

  // Persist active draft id across reloads.
  useEffect(() => {
    activeDraftIdStorage.set(activeDraftId);
  }, [activeDraftId]);
}
